import React from 'react';
import { RaceAsset } from '../../contexts/BettingProvider';
import { AssetPlacementHistory, TugOfWarBar } from '../../types';
import CryptoCar from './CryptoCar';
import { formatAssetPrice } from '../../utils/assetHelpers';

// Race Lane Component - Individual lane for each asset
interface RaceLaneProps {
  asset: RaceAsset;
  laneIndex: number;
  totalLanes: number;
  carPosition: number; // Now represents VERTICAL position (Y-axis percentage)
  performance: RaceAsset & { realTimePerformance: number };
  performanceRanking: number; // 0 = best performer, 1 = worst (1v1)
  isMobile: boolean;
  getAssetCSSColor: (symbol: string) => string;
  socialData?: any;
  // Betting selection props
  onLaneSelect?: () => void;
  isSelected?: boolean;
  selectionPosition?: number | null; // 1, 2, or 3 for placement position
  isBettingEnabled?: boolean;
  hasActiveBet?: boolean; // NEW: Check if user has already placed a bet
  totalBetVolume?: number; // Total volume bet on this asset across all users
  currentPhase?: string; // Current round phase to control visual effects
  bettingTimeRemaining?: number | null; // Time remaining in betting phase (for transition)
  // Placement history for betting phase
  placementHistory?: AssetPlacementHistory;
  targetSelectionPosition?: number; // 1, 2, or 3 - which position is currently being selected (to dim other medals)
  fullRaceHistory?: Array<{
    first: { symbol: string; percentage: number; fireIntensity: number } | null;
    second: { symbol: string; percentage: number; fireIntensity: number } | null;
    third: { symbol: string; percentage: number; fireIntensity: number } | null;
  }>; // Full podium history with fire intensities for each position
  isVerticalLayout?: boolean; // NEW: Flag for vertical racing layout
  betAmount?: number; // Current bet amount for payout calculation
  tugOfWarBars?: TugOfWarBar[]; // Tug-of-war history bars for betting phase
}

const RaceLane: React.FC<RaceLaneProps> = ({ 
  asset, 
  laneIndex, 
  totalLanes, 
  carPosition, 
  performance,
  performanceRanking,
  isMobile, 
  getAssetCSSColor,
  socialData,
  onLaneSelect,
  isSelected = false,
  selectionPosition = null,
  isBettingEnabled = false,
  hasActiveBet = false,
  bettingTimeRemaining = null,
  totalBetVolume = 0,
  currentPhase,
  placementHistory,
  targetSelectionPosition = 1,
  fullRaceHistory = [],
  isVerticalLayout = false,
  betAmount = 0.01,
  tugOfWarBars = []
}) => {
  // VERTICAL LAYOUT: Lanes are side-by-side columns (left-to-right)
  const laneWidth = 100 / totalLanes; // Width of each vertical column
  const laneLeft = (laneIndex * 100) / totalLanes; // Left position of column
  
  const performanceDisplay = performance.realTimePerformance >= 0 ? 
    `+${performance.realTimePerformance.toFixed(3)}%` : 
    `${performance.realTimePerformance.toFixed(3)}%`;

  const [isHovered, setIsHovered] = React.useState(false);
  
  // Determine if lane should be clickable - disabled when user has already placed a bet
  const isLaneClickable = isBettingEnabled && onLaneSelect && !hasActiveBet;
  
  // ✅ Check if we're in demo mode (no phase or undefined)
  const isDemoMode = !currentPhase;

  return (
    <div 
      className={`race-lane ${isVerticalLayout ? 'vertical-lane' : ''}`}
      style={{
        position: 'absolute',
        // VERTICAL LAYOUT: Side-by-side columns
        left: `${laneLeft}%`,
        width: `${laneWidth}%`,
        height: '100%',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRight: laneIndex < totalLanes - 1 ? '2px solid rgba(255, 255, 255, 0.1)' : 'none',
        cursor: isLaneClickable ? 'pointer' : 'default',
        transition: 'background 0.2s ease'
      }}
      onClick={isLaneClickable ? onLaneSelect : undefined}
      onMouseEnter={() => isLaneClickable && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Hover effect - shows when hovering and not selected */}
      {isHovered && !isSelected && isLaneClickable && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(160, 25, 255, 0.05)',
            border: '1px solid rgba(160, 25, 255, 0.3)',
            borderRadius: '4px',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />
      )}
    
      {/* Selection Overlay - shows when lane is selected */}
      {isSelected && selectionPosition && isLaneClickable && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(160, 25, 255, 0.08)',
            border: '1px solid rgba(160, 25, 255, 0.4)',
            borderRadius: '4px',
            zIndex: 3,
            pointerEvents: 'none'
          }}
        />
      )}
      
      {/* Lane Symbol and Price - TOP of vertical lane */}
      <div 
        className="lane-symbol"
        style={{
          position: 'absolute',
          top: isMobile ? '6px' : '8px', // Reduced from 8px/12px for more compact spacing
          left: '50%',
          transform: 'translateX(-50%)',
          width: isMobile ? '85px' : '105px', // Slightly narrower
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '3px' : '5px', // Tighter gap
          padding: isMobile ? '5px' : '7px', // Tighter padding
          zIndex: 25,
          background: 'rgba(0, 0, 0, 0.75)',
          borderRadius: '8px',
          border: `2px solid ${getAssetCSSColor(asset.symbol)}50`
        }}
      >
        {/* Asset Logo and Symbol */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '5px' : '6px' }}>
          <div 
            style={{
              width: isMobile ? '10px' : '13px',
              height: isMobile ? '10px' : '13px',
              borderRadius: '50%',
              backgroundColor: getAssetCSSColor(asset.symbol),
              boxShadow: `0 0 6px ${getAssetCSSColor(asset.symbol)}`
            }}
          />
          <span 
            style={{
              fontSize: isMobile ? '0.75rem' : '0.9rem',
              fontWeight: 800,
              color: '#FFFFFF',
              textShadow: `0 0 6px ${getAssetCSSColor(asset.symbol)}80, 0 1px 3px rgba(0, 0, 0, 0.9)`,
              letterSpacing: isMobile ? '0.4px' : '0.7px'
            }}
          >
            {asset.symbol}
          </span>
        </div>
        
        {/* Current Price */}
        <span 
          style={{
            fontSize: isMobile ? '0.6rem' : '0.7rem',
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.3px',
            textShadow: '0 0 8px rgba(255, 255, 255, 0.5), 0 1px 3px rgba(0, 0, 0, 0.9)'
          }}
        >
          {formatAssetPrice(asset.currentPrice, isMobile)}
        </span>
      </div>

      {/* Conditional: Show tug-of-war history during betting, show car during racing */}
      {currentPhase === 'BETTING' && bettingTimeRemaining != null && bettingTimeRemaining >= 1 && tugOfWarBars.length > 0 ? (
        /* BETTING PHASE: Show tug-of-war history bars */
        <>
          {/* Only render tug-of-war on the FIRST lane (left side) - spans across center */}
          {laneIndex === 0 && (() => {
            // Calculate win counts for each side
            const barsToShow = tugOfWarBars.slice(0, 10);
            const totalRaces = barsToShow.length;
            const leftAsset = asset.symbol; // First lane asset
            const rightAsset = barsToShow.length > 0 ? barsToShow[0].loser === leftAsset ? barsToShow[0].winner : barsToShow[0].loser : '';
            const leftWins = barsToShow.filter(bar => bar.winner === leftAsset).length;
            const rightWins = totalRaces - leftWins;
            
            return (
            <div 
              className="tug-of-war-container"
              style={{
                position: 'absolute',
                left: isMobile ? '30%' : '25%', // Inset from left edge
                width: isMobile ? '140%' : '150%', // Span both lanes but stay within track
                top: isMobile ? '95px' : '115px', // Start below race rules text
                bottom: isMobile ? '20px' : '25px', // End lower (more space)
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly', // Even distribution of bars
                padding: isMobile ? '4px 2px' : '6px 6px',
                zIndex: 10
              }}
            >
              {/* Win record labels - just above bars, aligned horizontally with asset symbols */}
              {/* Left asset win count - towards left edge */}
              <span style={{
                position: 'absolute',
                top: isMobile ? '-18px' : '-22px',
                left: isMobile ? '5%' : '12%',
                fontSize: isMobile ? '0.6rem' : '0.7rem',
                fontWeight: 700,
                color: getAssetCSSColor(leftAsset),
                textShadow: `0 0 6px ${getAssetCSSColor(leftAsset)}60`
              }}>
                {leftWins}/{totalRaces}
              </span>
              
              {/* Right asset win count - towards right edge */}
              <span style={{
                position: 'absolute',
                top: isMobile ? '-18px' : '-22px',
                right: isMobile ? '5%' : '12%',
                fontSize: isMobile ? '0.6rem' : '0.7rem',
                fontWeight: 700,
                color: getAssetCSSColor(rightAsset),
                textShadow: `0 0 6px ${getAssetCSSColor(rightAsset)}60`
              }}>
                {rightWins}/{totalRaces}
              </span>
              
              {/* Tug-of-war bars - oldest at top, most recent at bottom (read top-to-bottom) */}
              {[...barsToShow].reverse().map((bar, idx) => {
                const winnerColor = getAssetCSSColor(bar.winner);
                const isWinnerOnLeft = bar.winner === asset.symbol; // Left lane = first asset
                const barWidth = bar.normalizedWidth * 100; // Convert to percentage
                const percentText = bar.dominancePercent > 0 ? `${Math.round(bar.dominancePercent)}%` : '<1%';
                
                return (
                  <div 
                    key={idx}
                    className="tug-of-war-bar-row"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: isMobile ? '22px' : '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {/* Left side bar (extends left from center) */}
                    <div 
                      style={{
                        position: 'absolute',
                        right: '50%',
                        height: isMobile ? '18px' : '24px',
                        width: isWinnerOnLeft ? `${barWidth / 2}%` : `${barWidth / 4}%`,
                        background: isWinnerOnLeft 
                          ? `linear-gradient(to left, ${winnerColor}, ${winnerColor}80)`
                          : 'linear-gradient(to left, rgba(80, 80, 80, 0.35), rgba(50, 50, 50, 0.2))',
                        borderRadius: '3px 0 0 3px',
                        boxShadow: isWinnerOnLeft ? `0 0 8px ${winnerColor}60` : 'none',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        paddingLeft: isMobile ? '3px' : '5px',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Percentage text inside left bar (if winner) */}
                      {isWinnerOnLeft && (
                        <span style={{
                          fontSize: isMobile ? '0.5rem' : '0.6rem',
                          fontWeight: 700,
                          color: '#FFFFFF',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          whiteSpace: 'nowrap'
                        }}>
                          {percentText}
                        </span>
                      )}
                    </div>
                    
                    {/* Right side bar (extends right from center) */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '50%',
                        height: isMobile ? '18px' : '24px',
                        width: !isWinnerOnLeft ? `${barWidth / 2}%` : `${barWidth / 4}%`,
                        background: !isWinnerOnLeft 
                          ? `linear-gradient(to right, ${winnerColor}, ${winnerColor}80)`
                          : 'linear-gradient(to right, rgba(80, 80, 80, 0.35), rgba(50, 50, 50, 0.2))',
                        borderRadius: '0 3px 3px 0',
                        boxShadow: !isWinnerOnLeft ? `0 0 8px ${winnerColor}60` : 'none',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: isMobile ? '3px' : '5px',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Percentage text inside right bar (if winner) */}
                      {!isWinnerOnLeft && (
                        <span style={{
                          fontSize: isMobile ? '0.5rem' : '0.6rem',
                          fontWeight: 700,
                          color: '#FFFFFF',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          whiteSpace: 'nowrap'
                        }}>
                          {percentText}
                        </span>
                      )}
                    </div>
                    
                    {/* Center divider tick */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '2px',
                        height: isMobile ? '16px' : '18px',
                        background: 'rgba(255, 255, 255, 0.3)',
                        zIndex: 2
                      }}
                    />
                  </div>
                );
              })}
              
              {/* Recent label at bottom */}
              {/* Bottom label */}
              <div 
                style={{
                  position: 'absolute',
                  bottom: isMobile ? '-14px' : '-18px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: isMobile ? '0.45rem' : '0.55rem',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap'
                }}
              >
                MOST RECENT | % DOMINANCE
              </div>
            </div>
          );
          })()}
        </>
      ) : (
        /* RACING PHASE: Show car - VERTICAL layout (car moves up/down) */
        <div 
            className="lane-car-vertical"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: `${carPosition}%`, // Y position from bottom
              transform: 'translateX(-50%)',
              zIndex: 10,
              transformStyle: 'preserve-3d'
            }}
          >
            <CryptoCar 
              symbol={asset.symbol} 
              isVertical={true}
              showExhaustStreaks={true}
              isRacing={isDemoMode || currentPhase === 'RACING' || currentPhase === 'SETTLEMENT'}
              driftPattern={laneIndex % 2 === 0 ? 1 : 2}
              laneIndex={laneIndex}
            />
            
            {/* Clean Race Status HUD - Just Position + Performance */}
            {(isDemoMode || currentPhase === 'RACING' || currentPhase === 'SETTLEMENT' || currentPhase === 'RESULTS') && (
            <div 
              style={{
                position: 'absolute',
                bottom: '100%',
                marginBottom: isMobile ? '1px' : '2px', // Tighter spacing
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '2px' : '3px', // Tighter gap
                background: 'rgba(0, 0, 0, 0.92)',
                padding: isMobile ? '2px 4px' : '2px 5px', // Slightly tighter padding
                borderRadius: isMobile ? '4px' : '5px',
                zIndex: 20
              }}
            >
              {/* Position medal - only show gold for winner (1st place) */}
              {performanceRanking === 0 && (
                <span 
                  style={{
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    lineHeight: 1,
                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))'
                  }}
                >
                  🥇
                </span>
              )}
              
              {/* Performance % - Use car color */}
              <span 
                style={{
                  fontSize: isMobile ? '0.65rem' : '0.7rem',
                  fontWeight: 700,
                  color: getAssetCSSColor(asset.symbol),
                  textShadow: `0 0 8px ${getAssetCSSColor(asset.symbol)}`,
                  fontFamily: 'Monaco, monospace',
                  letterSpacing: '-0.3px'
                }}
              >
                {performanceDisplay}
              </span>
            </div>
          )}
        </div>
      )}
      
    </div>
  );
};

export default RaceLane;

